import { useState } from 'react';
import axios from 'axios';
import { PhoneCall } from 'lucide-react';
import { Label } from '../ui/label';
import { ConsentFields, LegalNote, TextField } from './ContactForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', phone: '', email: '', topic: '', bestTime: '', smsNotifications: false, smsMarketing: false };
const SELECT = 'w-full h-12 rounded-xl bg-white/5 border border-white/15 text-white px-3 focus:outline-none focus:ring-2 focus:ring-orange-500 [&>option]:bg-[#0A1128]';

const Choice = ({ id, label, field, options, form, setForm }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-slate-200">{label}</Label>
    <select id={id} data-testid={`${id}-select`} required value={form[field]}
      onChange={(e) => setForm({ ...form, [field]: e.target.value })} className={SELECT}>
      <option value="" disabled>Select one</option>
      {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
    </select>
  </div>
);

export const CallbackForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/callbacks`, form);
      setStatus('ok');
      setForm(EMPTY);
    } catch {
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="callback-form" className="glass-panel rounded-3xl p-7 md:p-10 space-y-6">
      <div className="grid md:grid-cols-2 gap-5">
        <TextField id="callback-name" label="Name *" field="name" form={form} setForm={setForm} required placeholder="Your name" />
        <TextField id="callback-phone" label="Phone *" field="phone" form={form} setForm={setForm} type="tel" required minLength={7} placeholder="(555) 555-5555" />
      </div>
      <TextField id="callback-email" label="Email *" field="email" form={form} setForm={setForm} type="email" required placeholder="your@email.com" />
      <div className="grid md:grid-cols-2 gap-5">
        <Choice id="callback-topic" label="Interested in *" field="topic" form={form} setForm={setForm}
          options={[['Term Life', 'Term life'], ['Whole Life', 'Whole life'], ['IUL', 'Indexed universal life (IUL)'], ['Final Expense', 'Final expense']]} />
        <Choice id="callback-best-time" label="Best time to call *" field="bestTime" form={form} setForm={setForm}
          options={[['Morning', 'Morning (9am – 12pm)'], ['Afternoon', 'Afternoon (12pm – 4pm)'], ['Evening', 'Evening (4pm – 7pm)']]} />
      </div>
      <ConsentFields prefix="callback" form={form} setForm={setForm} />
      <LegalNote />
      <button type="submit" disabled={sending} data-testid="callback-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-4 text-base">
        {sending ? 'Sending...' : 'Request a Call Back'} <PhoneCall className="w-4 h-4" />
      </button>
      {status === 'ok' && <p data-testid="callback-success-message" className="text-center text-emerald-400 text-sm">Thanks! We'll call you back at your preferred time.</p>}
      {status === 'error' && <p data-testid="callback-error-message" className="text-center text-red-400 text-sm">Something went wrong. Please try again or call us at (443) 293-5042.</p>}
    </form>
  );
};
